import React from 'react';
import { useTranslation } from 'react-i18next';
import { FeeInfo } from '@/components/LargeOrderFee/LargeOrderFeeInfo';

export interface CostBreakdownBox {
  sequence: number;
  size: string;
  fee: number;
  charged: number;
  /** BOXY-301 (owner 2026-10-22): main box = the order fee, extra box = a flat fee by size. */
  role?: 'main' | 'extra';
  is_primary?: boolean;
}

export interface CostBreakdownProps {
  currency: string;
  products: Array<{ label: string; amount: number }>;
  /**
   * Who pays the shipping: the customer's part is added to what they pay, the
   * merchant's part is deducted from what the merchant receives. Usually one of
   * the two is 0; both are set when a customer-paid fee is partly subsidised.
   */
  shipping: { name?: string; customerAmount: number; merchantAmount: number };
  /** Per-box breakdown of the shipping fee; multi-box orders only. */
  boxes?: CostBreakdownBox[];
  largeOrderFee?: { amount: number; reason?: { title: string; message: string } };
  customerPays: number;
  youReceive: number;
}

const money = (amount: number, currency: string) =>
  `${Math.round(amount).toLocaleString()} ${currency}`;

/**
 * One reading order for every cost summary: only lines that change what the
 * merchant receives carry a sign; the boxes are the grey breakdown of the
 * shipping line (no saving line, BOXY-574).
 */
const CostBreakdown: React.FC<CostBreakdownProps> = ({
  currency,
  products,
  shipping,
  boxes,
  largeOrderFee,
  customerPays,
  youReceive,
}) => {
  const { t } = useTranslation();
  const byCustomer = shipping.customerAmount > 0;
  const shippingLabel = `${String(t('shipping_label', 'Shipping'))}${shipping.name ? ` · ${shipping.name}` : ''}`;

  const row = (label: React.ReactNode, value: string, strong = false, testId?: string) => (
    <div data-testid={testId} className='flex items-center justify-between gap-3 py-2'>
      <span
        className={`min-w-0 text-sm ${strong ? 'font-semibold text-sd-foreground' : 'text-sd-muted-foreground'}`}
      >
        {label}
      </span>
      <span
        dir='ltr'
        className={`shrink-0 text-sm tabular-nums ${strong ? 'font-semibold text-sd-foreground' : 'font-medium'}`}
      >
        {value}
      </span>
    </div>
  );

  const customerPaysRow = row(
    String(t('customer_pays', 'Customer pays')),
    money(customerPays, currency),
    false,
    'cost-customer-pays',
  );

  return (
    <div className='flex flex-col [&>*+*]:border-0 [&>*+*]:border-t [&>*+*]:border-solid [&>*+*]:border-sd-border'>
      <div>
        {products.map((p, i) => (
          <div key={i}>{row(p.label, money(p.amount, currency))}</div>
        ))}
      </div>

      {byCustomer ? null : customerPaysRow}

      <div data-testid='cost-shipping'>
        {byCustomer
          ? row(
              `${shippingLabel} · ${String(t('paid_by_customer', 'paid by customer'))}`,
              `+ ${money(shipping.customerAmount, currency)}`,
            )
          : null}
        {shipping.merchantAmount > 0 || !byCustomer
          ? row(
              byCustomer
                ? `${shippingLabel} · ${String(t('paid_by_you', 'paid by you'))}`
                : shippingLabel,
              `− ${money(shipping.merchantAmount, currency)}`,
            )
          : null}
        {boxes && boxes.length >= 2 ? (
          <div className='mb-2 ms-3 space-y-1 border-s-2 border-solid border-y-0 border-e-0 border-sd-border ps-3'>
            {boxes.map((box) => (
              <div
                key={box.sequence}
                data-testid='cost-box-line'
                className='flex items-center justify-between text-xs text-sd-muted-foreground'
              >
                <span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
                  {/* §5.1 (owner 2026-10-22): each line says main box (order fee)
                      or extra box (flat fee by size); amounts, never a percentage. */}
                  <span dir='ltr'>{`BOX ${box.sequence}/${boxes.length} · ${box.size}`}</span>
                  {box.role || box.is_primary !== undefined ? (
                    <span className='text-[10px] font-medium'>
                      {String(
                        t(
                          (box.role ?? (box.is_primary ? 'main' : 'extra')) === 'main'
                            ? 'box_role_main'
                            : 'box_role_extra',
                        ),
                      )}
                    </span>
                  ) : null}
                </span>
                <span dir='ltr' className='tabular-nums'>
                  {money(box.charged, currency)}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {largeOrderFee?.amount ? (
        <div data-testid='cost-large-order-fee'>
          {row(
            <span className='inline-flex items-center gap-1.5'>
              {String(t('large_order_fee', 'Large order fee'))}
              {largeOrderFee.reason ? (
                <FeeInfo
                  title={largeOrderFee.reason.title}
                  message={largeOrderFee.reason.message}
                />
              ) : null}
            </span>,
            `− ${money(largeOrderFee.amount, currency)}`,
          )}
        </div>
      ) : null}

      {byCustomer ? customerPaysRow : null}

      <div data-testid='cost-you-receive' className='pt-1'>
        {row(String(t('you_receive', 'You receive')), money(youReceive, currency), true)}
      </div>
    </div>
  );
};

export default CostBreakdown;
